import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { api } from "../lib/api";

// In-memory cache so we don't hit /api/cms on every route change.
let _cmsCache = null;
let _cmsFetchedAt = 0;
const CMS_TTL_MS = 60 * 1000;

async function getCachedCms() {
  const now = Date.now();
  if (_cmsCache && now - _cmsFetchedAt < CMS_TTL_MS) return _cmsCache;
  try {
    const { data } = await api.get("/cms");
    _cmsCache = data;
    _cmsFetchedAt = now;
    return data;
  } catch {
    return _cmsCache || null;
  }
}

// Invalidate the cache after an admin save (called from AdminCMS save handler).
export function invalidateCmsCache() {
  _cmsCache = null;
  _cmsFetchedAt = 0;
}

function setMeta(name, value, attr = "name") {
  if (!value) {
    document.head.querySelector(`meta[${attr}='${name}']`)?.remove();
    return;
  }
  let el = document.head.querySelector(`meta[${attr}='${name}']`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", value);
}

function setCanonical(href) {
  let el = document.head.querySelector("link[rel='canonical']");
  if (!href) {
    el?.remove();
    return;
  }
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function pickSeo(seo, path) {
  if (!seo) return null;
  // Longest-prefix match (so /products/widget matches /products before /).
  const keys = Object.keys(seo).filter((k) => k.startsWith("/"));
  keys.sort((a, b) => b.length - a.length);
  const exact = seo[path];
  const prefixMatch = keys.find((k) => path === k || (k !== "/" && path.startsWith(k + "/")));
  const fallback = seo[prefixMatch] || seo["/"] || null;
  // Treat empty strings as "use the next-level fallback" instead of overriding
  // with an empty value (so admins can't accidentally clear the <title> globally).
  const stripEmpty = (obj) => Object.fromEntries(Object.entries(obj || {}).filter(([, v]) => v !== "" && v !== null && v !== undefined));
  return {
    ...(seo.default || {}),
    ...stripEmpty(fallback || {}),
    ...stripEmpty(exact || {}),
  };
}

/**
 * useDocumentSeo — writes <title> + meta description + OG tags + canonical
 * to <head> based on the admin-edited CMS `seo` block.
 *
 * Path-prefix match: /products/foo will pick up /products SEO first, then /.
 */
export function useDocumentSeo() {
  const { pathname } = useLocation();
  const [cms, setCms] = useState(null);

  useEffect(() => {
    let alive = true;
    getCachedCms().then((d) => { if (alive) setCms(d); });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (!cms) return;
    const meta = pickSeo(cms.seo || {}, pathname);
    if (!meta) return;
    if (meta.title) document.title = meta.title;
    setMeta("description", meta.description || "");
    setMeta("og:title", meta.title || "", "property");
    setMeta("og:description", meta.description || "", "property");
    setMeta("og:image", meta.og_image || "", "property");
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", meta.title || "");
    setMeta("twitter:description", meta.description || "");
    if (meta.og_image) setMeta("twitter:image", meta.og_image);
    setCanonical(meta.canonical || "");
  }, [cms, pathname]);
}
